import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {StyledSettingsFormField} from '../../../../libs/shared/ui/src/components/settings/settingsWrapper';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {CircleModel} from '../../../models/api/circleModel';
import {ChallengesEnum} from '../../../models/api/preferencesModel';
import {DepartmentsEnum} from '../../../models/api/userMetadataModel';
import {SettingsInboxesNewCreatorState} from '../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {getActivationPreferences} from '../../../store/data/preferences/preferencesSelectors';
import {onboardingGuideSlideHeight, onboardingGuideSlideWidth} from '../guide/onboardingGuideHelpers';
import {OnboardingGuideSlide, OnboardingGuideSlideNavigationProps} from '../guide/onboardingGuideSlide';
import {StyledOnboardingFormTitleDiv, StyledOnboardingFormTitlesDiv} from './onboardingWelcomeHelpers';

/*
 * Constants.
 */

export const onboardingInboxAccessAll = 'ALL';

/*
 * Props.
 */

export interface OnboardingInboxSlideProps extends OnboardingGuideSlideNavigationProps {
  createdCircle: CircleModel | undefined;
  inboxState: SettingsInboxesNewCreatorState;
  onSetName: (name: string) => void;

  onSetIsTicketingInbox: (isTicketingInbox: boolean) => void;
  selectedTeamType: DepartmentsEnum | string;
}

export type OnboardingInboxAccess = typeof onboardingInboxAccessAll | number;

/*
 * Style.
 */

const StyledLeftDiv = styled.div`
  position: relative;
  grid-template-areas:
    'description'
    'form'
    'footer';

  flex: 0 0 100%;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;

  width: 430px;
`;

const StyledDescriptionDiv = styled.div`
  grid-area: description;

  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 1.29;

  width: 456px;
`;

const StyledFieldsDiv = styled.div`
  padding-top: 32px;

  grid-area: form;
`;

const StyledCheckboxWrapper = styled.div`
  margin-top: 24px;
`;

const StyledCheckboxDescription = styled.div`
  margin-left: 26px;
  margin-top: 4px;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const OnboardingInboxSlide: FC<OnboardingInboxSlideProps> = (props) => {
  const {inboxState, onSetName, selectedTeamType, navPrimary, navSecondary, onSetIsTicketingInbox} = props;

  // State.
  const {name, isTicketingInbox} = inboxState;

  const activationPreferences = useAppSelector(getActivationPreferences);

  const shouldHaveTicketingExperience =
    activationPreferences.questionnaireChallenges?.selected.includes(ChallengesEnum.MULTI_CHANNEL_SUPPORT) ||
    activationPreferences.questionnaireChallenges?.selected.includes(ChallengesEnum.INTERNAL_REQUESTS) ||
    selectedTeamType === DepartmentsEnum.CUSTOMER_SUPPORT ||
    selectedTeamType === DepartmentsEnum.CLIENT_SERVICES ||
    selectedTeamType === DepartmentsEnum.INTERNAL_HELP_DESK;

  useEffect(() => {
    if (isTicketingInbox === undefined && shouldHaveTicketingExperience) {
      onSetIsTicketingInbox(true);
    }
  }, [isTicketingInbox, shouldHaveTicketingExperience, onSetIsTicketingInbox]);

  const renderCustomSuggestions = () => {
    switch (selectedTeamType) {
      case DepartmentsEnum.ACCOUNT_MANAGEMENT:
        return (
          <Trans id="onboardinginbox-name-description-accountmanagement">
            e.g. Account Services, Contracts, New Business
          </Trans>
        );
      case DepartmentsEnum.BROKERAGE:
        return (
          <Trans id="onboardinginbox-name-description-brokerage">e.g. Client Inquiries, Data, Invoices</Trans>
        );
      case DepartmentsEnum.CLIENT_SERVICES:
        return (
          <Trans id="onboardinginbox-name-description-clientservices">
            e.g. Client Inquiries, Client Feedback, Priority
          </Trans>
        );
      case DepartmentsEnum.CUSTOMER_SUPPORT:
        return (
          <Trans id="onboardinginbox-name-description-customersupport">
            e.g. Customer Success, Escalations, Support Tier 1
          </Trans>
        );
      case DepartmentsEnum.DESIGN:
        return (
          <Trans id="onboardinginbox-name-description-design">
            e.g. Brand Requests, Customer Feedback, Research
          </Trans>
        );
      case DepartmentsEnum.ENGINEERING:
        return (
          <Trans id="onboardinginbox-name-description-engineering">
            e.g. Deliverability, On-call Alerts, Support Tier 1
          </Trans>
        );
      case DepartmentsEnum.EXECUTIVE:
        return (
          <Trans id="onboardinginbox-name-description-executive">
            e.g. Executive Assistant, Priority, Urgent
          </Trans>
        );
      case DepartmentsEnum.FINANCE:
        return (
          <Trans id="onboardinginbox-name-description-finance">
            e.g. Accounts Payable, Accounts Receivable, Invoices
          </Trans>
        );
      case DepartmentsEnum.LEGAL:
        return <Trans id="onboardinginbox-name-description-legal">e.g. Compliance, Contracts, Privacy</Trans>;
      case DepartmentsEnum.MARKETING:
        return (
          <Trans id="onboardinginbox-name-description-marketing">
            e.g. Marketing, Marketing Ops, Outbound Alerts
          </Trans>
        );
      case DepartmentsEnum.OPERATIONS:
        return (
          <Trans id="onboardinginbox-name-description-operations">e.g. Customer Ops, Requests, Urgent</Trans>
        );
      case DepartmentsEnum.PRODUCT:
        return (
          <Trans id="onboardinginbox-name-description-product">
            e.g. Customer Feedback, Product, Product Insights
          </Trans>
        );
      case DepartmentsEnum.PROJECT_MANAGEMENT:
        return (
          <Trans id="onboardinginbox-name-description-projectmanagement">
            e.g. Budget Updates, New Inquiries, Urgent
          </Trans>
        );
      case DepartmentsEnum.SALES:
        return <Trans id="onboardinginbox-name-description-sales">e.g. Leads, New Business, Sales</Trans>;
      case DepartmentsEnum.INTERNAL_HELP_DESK:
        return (
          <Trans id="onboardinginbox-name-description-internalhelpdesk">e.g. HR, Finance, Legal, IT</Trans>
        );
      default:
        return <Trans id="onboardinginbox-name-description-default">e.g. Feedback, Requests, Urgent</Trans>;
    }
  };

  return (
    <OnboardingGuideSlide
      width={onboardingGuideSlideWidth}
      height={onboardingGuideSlideHeight}
      navSecondary={navSecondary}
      navPrimary={navPrimary}
      isLeftContent
    >
      <StyledLeftDiv>
        {/* Title */}
        <StyledTitleDiv>
          <StyledOnboardingFormTitlesDiv>
            <StyledOnboardingFormTitleDiv>
              <Trans id="onboardinginbox-title">Next, create a shared inbox for your team</Trans>
            </StyledOnboardingFormTitleDiv>
          </StyledOnboardingFormTitlesDiv>
        </StyledTitleDiv>
        {/* Description */}
        <StyledDescriptionDiv>
          <Trans id="onboardinginbox-description">
            An inbox is a centralized space for your team to access messages coming from multiple channels
            (i.e. email, SMS, Facebook, etc.)
          </Trans>
        </StyledDescriptionDiv>
        {/* Fields */}
        <StyledFieldsDiv>
          <StyledSettingsFormField>
            <TextBox
              label={<Trans id="onboardinginbox-name">Inbox name</Trans>}
              value={name}
              onChange={onSetName}
              message={renderCustomSuggestions()}
              placeholder="Add inbox name"
              isRequired
              shouldAutoFocus
            />
          </StyledSettingsFormField>
          <StyledCheckboxWrapper>
            <CheckBox
              isChecked={isTicketingInbox ?? shouldHaveTicketingExperience}
              onChange={onSetIsTicketingInbox}
            >
              <Trans id="onboardinginbox-manage-as-tickets">
                Manage conversations in this inbox as tickets
              </Trans>
            </CheckBox>
            <StyledCheckboxDescription>
              <Trans id="onboardinginbox-manage-as-tickets-description">
                Conversations will have ticket statuses (open, waiting, resolved) and ticket IDs.
              </Trans>
            </StyledCheckboxDescription>
          </StyledCheckboxWrapper>
        </StyledFieldsDiv>
      </StyledLeftDiv>
    </OnboardingGuideSlide>
  );
};
